import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Autocomplete,
} from "@mui/material";
import {
  Api as ApiIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  AddCircleOutlineOutlined,
  Close as CloseIcon,
  DataObject as DataObjectIcon,
  Image as ImageIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import apiService from "../../../service/Api";
import type { EndpointRequest, HttpMethod, Priority } from "../types";
import { JsonEditorInput } from "./JsonEditorInput";
import { EndpointTemplatesModal, EndpointTemplate } from "./EndpointTemplatesModal";

interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string;
      detail?: string;
      [key: string]: unknown;
    };
  };
}

interface CreateRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (request: Omit<EndpointRequest, "id">) => void;
}

const HTTP_METHODS: HttpMethod[] = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const PRIORITIES: Priority[] = ["low", "medium", "high", "urgent"];

interface Component {
  id: number;
  name: string;
  description?: string;
}

interface DevUser {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

export const CreateRequestDialog: React.FC<CreateRequestDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";

  const [activeStep, setActiveStep] = useState(0);
  const [components, setComponents] = useState<Component[]>([]);
  const [loadingComponents, setLoadingComponents] = useState(false);
  const [developers, setDevelopers] = useState<DevUser[]>([]);
  const [loadingDevelopers, setLoadingDevelopers] = useState(false);
  const [selectedDevOption, setSelectedDevOption] = useState<DevUser | string | null>(null);
  const [typedDevEmail, setTypedDevEmail] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [openTemplatesModal, setOpenTemplatesModal] = useState(false);

  const [formData, setFormData] = useState<Omit<EndpointRequest, "id">>({
    component: "",
    task: "",
    lead: "",
    method: "GET",
    status: "pending",
    priority: "medium",
    requestDate: new Date().toISOString().split("T")[0],
    targetDeliveryDate: "",
    expectedDeliveryDate: "",
    challenges: "",
    description: "",
    payload: "",
  });

  const [requestDate, setRequestDate] = useState<Dayjs | null>(dayjs());
  const [targetDate, setTargetDate] = useState<Dayjs | null>(null);
  const [expectedDate, setExpectedDate] = useState<Dayjs | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [openAddComponentDialog, setOpenAddComponentDialog] = useState(false);
  const [newComponentData, setNewComponentData] = useState<{
    name: string;
    description: string;
  }>({ name: "", description: "" });
  const [savingNewComponent, setSavingNewComponent] = useState(false);

  useEffect(() => {
    if (open) {
      fetchComponents();
      fetchDevelopers();
      setActiveStep(0);
      if (user?.username) {
        setFormData((prev) => ({ ...prev, lead: user.username }));
      }
    }
  }, [open, user]);

  const fetchComponents = async () => {
    setLoadingComponents(true);
    try {
      const response = await apiService.get<{ results?: Component[] } | Component[]>(
        "/api/endpoint-request/components/"
      );
      const componentsData = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];
      setComponents(componentsData);
    } catch (error: unknown) {
      console.error("Failed to fetch components:", error);
      toast.error("Failed to load components. Please try again.");
    } finally {
      setLoadingComponents(false);
    }
  };

  const fetchDevelopers = async () => {
    setLoadingDevelopers(true);
    try {
      const response = await apiService.get<{ results?: DevUser[] } | DevUser[]>(
        "/api/authentication/users/?page_size=100"
      );
      const usersData = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];
      setDevelopers(usersData);
    } catch (error: unknown) {
      console.error("Failed to fetch developers:", error);
    } finally {
      setLoadingDevelopers(false);
    }
  };

  const handleSelectTemplate = (template: EndpointTemplate) => {
    setFormData((prev) => ({
      ...prev,
      method: template.method,
      priority: template.priority,
      task: template.taskDescription,
      payload: template.samplePayload,
    }));
    toast.info(`Loaded "${template.name}" preset template!`);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageFile(null);
  };

  const handleSaveNewComponent = async () => {
    if (!newComponentData.name.trim()) {
      toast.error("Component name is required");
      return;
    }

    setSavingNewComponent(true);
    try {
      const response = await apiService.post<Component>(
        "/api/endpoint-request/components/",
        {
          name: newComponentData.name.trim(),
          description: newComponentData.description.trim() || "",
        }
      );
      const savedComponent = response.data;
      toast.success("Component added successfully!");

      setComponents((prev) => [...prev, savedComponent]);
      setFormData((prev) => ({
        ...prev,
        component: savedComponent.id.toString(),
      }));

      setOpenAddComponentDialog(false);
      setNewComponentData({ name: "", description: "" });
    } catch (error: unknown) {
      console.error("Failed to save new component:", error);
      const apiError = error as ApiErrorResponse;
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.detail ||
        "Failed to add component. Please try again.";
      toast.error(errorMessage);
    } finally {
      setSavingNewComponent(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.component || !formData.task || !formData.lead) {
      toast.error("Please fill in all required fields (Component & Task description)");
      return;
    }

    let parsedPayload: Record<string, unknown> = {};
    if (formData.payload && formData.payload.trim()) {
      try {
        parsedPayload = JSON.parse(formData.payload) as Record<string, unknown>;
      } catch {
        toast.error("Invalid JSON in payload field. Please check your syntax.");
        return;
      }
    }

    const selectedComponent = components.find(
      (c) => c.id.toString() === formData.component || c.name === formData.component
    );
    if (!selectedComponent) {
      toast.error("Please select a valid component");
      return;
    }

    setSubmitting(true);
    try {
      const apiPayload: Record<string, unknown> = {
        component: selectedComponent.id,
        task_description: formData.task,
        http_method: formData.method || "GET",
        priority: formData.priority || "medium",
        additional_information: formData.description || "",
        endpoint_url: "",
      };

      let finalAssignedId: number | null = null;
      let finalAssignedEmail: string | null = null;

      if (typeof selectedDevOption === "object" && selectedDevOption !== null) {
        finalAssignedId = selectedDevOption.id;
        finalAssignedEmail = selectedDevOption.email || null;
      } else if (typeof selectedDevOption === "string" && selectedDevOption.trim()) {
        finalAssignedEmail = selectedDevOption.trim();
      } else if (typedDevEmail && typedDevEmail.trim()) {
        const match = developers.find(
          (d) => d.email && d.email.toLowerCase() === typedDevEmail.trim().toLowerCase()
        );
        if (match) {
          finalAssignedId = match.id;
          finalAssignedEmail = match.email;
        } else {
          finalAssignedEmail = typedDevEmail.trim();
        }
      }

      if (finalAssignedId) {
        apiPayload.assigned_backend = finalAssignedId;
      }
      if (finalAssignedEmail) {
        apiPayload.assigned_backend_email = finalAssignedEmail;
      }

      if (Object.keys(parsedPayload).length > 0) {
        apiPayload.payload = parsedPayload;
      }

      if (targetDate) apiPayload.target_delivery_date_time = targetDate.toISOString();
      if (expectedDate) apiPayload.expected_delivery_date = expectedDate.toISOString();
      if (formData.challenges) apiPayload.challenges = formData.challenges;

      let requestData: Record<string, unknown> | FormData;

      if (imageFile) {
        const formDataPayload = new FormData();
        formDataPayload.append("component", selectedComponent.id.toString());
        formDataPayload.append("task_description", formData.task);
        formDataPayload.append("http_method", formData.method || "GET");
        formDataPayload.append("priority", formData.priority || "medium");
        formDataPayload.append("additional_information", formData.description || "");
        formDataPayload.append("endpoint_url", "");

        if (finalAssignedId) {
          formDataPayload.append("assigned_backend", finalAssignedId.toString());
        }
        if (finalAssignedEmail) {
          formDataPayload.append("assigned_backend_email", finalAssignedEmail);
        }

        if (Object.keys(parsedPayload).length > 0) {
          formDataPayload.append("payload", JSON.stringify(parsedPayload));
        }

        if (targetDate) formDataPayload.append("target_delivery_date_time", targetDate.toISOString());
        if (expectedDate) formDataPayload.append("expected_delivery_date", expectedDate.toISOString());
        if (formData.challenges) formDataPayload.append("challenges", formData.challenges);

        formDataPayload.append("endpoint_screenshot_image", imageFile);
        requestData = formDataPayload;
      } else {
        requestData = apiPayload;
      }

      await apiService.post("/api/endpoint-request/endpoint-requests/", requestData);

      toast.success("Endpoint request created and assigned developer notified!");

      const submitData = {
        ...formData,
        requestDate: requestDate?.format("YYYY-MM-DD") || formData.requestDate,
        targetDeliveryDate: targetDate?.format("YYYY-MM-DD") || undefined,
        expectedDeliveryDate: expectedDate?.format("YYYY-MM-DD") || undefined,
      };
      onSubmit(submitData);
      handleClose();
    } catch (error: unknown) {
      console.error("Failed to create endpoint request:", error);
      const apiError = error as ApiErrorResponse;
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.detail ||
        "Failed to create endpoint request. Please try again.";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      component: "",
      task: "",
      lead: "",
      method: "GET",
      status: "pending",
      priority: "medium",
      requestDate: new Date().toISOString().split("T")[0],
      targetDeliveryDate: "",
      expectedDeliveryDate: "",
      challenges: "",
      description: "",
      payload: "",
    });
    setSelectedDevOption(null);
    setTypedDevEmail("");
    setRequestDate(dayjs());
    setTargetDate(null);
    setExpectedDate(null);
    setImagePreview(null);
    setImageFile(null);
    setActiveStep(0);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "20px",
              overflow: "hidden",
            },
          },
        }}
      >
        {/* Header */}
        <DialogTitle component="div" sx={{ p: 3, pb: 2, backgroundColor: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  p: 1.2,
                  borderRadius: "12px",
                  backgroundColor: `${primaryColor}15`,
                  color: primaryColor,
                  display: "flex",
                }}
              >
                <ApiIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="700">
                  Create New Endpoint Request
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Step-by-step API contract specification
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setOpenTemplatesModal(true)}
                sx={{ borderRadius: "10px", fontWeight: 700, textTransform: "none" }}
              >
                Load Preset Template
              </Button>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Step Navigation Tabs */}
          <Box sx={{ mt: 2 }}>
            <Tabs
              value={activeStep}
              onChange={(_, val) => setActiveStep(val)}
              sx={{
                minHeight: 36,
                "& .MuiTab-root": {
                  minHeight: 36,
                  py: 0.5,
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  textTransform: "none",
                },
              }}
            >
              <Tab label="1. General & Spec" icon={<ApiIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label="2. JSON Payload" icon={<DataObjectIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label="3. Screenshot & Timeline" icon={<ImageIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
            </Tabs>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/* STEP 0: General & Spec */}
            {activeStep === 0 && (
              <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth required sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}>
                    <InputLabel>Component *</InputLabel>
                    <Select
                      value={formData.component}
                      onChange={(e) => setFormData({ ...formData, component: e.target.value })}
                      label="Component *"
                      disabled={loadingComponents}
                    >
                      {components.map((c) => (
                        <MenuItem key={c.id} value={c.id.toString()}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    startIcon={<AddCircleOutlineOutlined />}
                    onClick={() => setOpenAddComponentDialog(true)}
                    sx={{ mt: 0.5, fontSize: "0.78rem", textTransform: "none", fontWeight: 600 }}
                  >
                    Create new component
                  </Button>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Requested By / Lead"
                    value={formData.lead}
                    onChange={(e) => setFormData({ ...formData, lead: e.target.value })}
                    slotProps={{ input: { sx: { borderRadius: "12px" } } }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}>
                    <InputLabel>HTTP Method</InputLabel>
                    <Select
                      value={formData.method || "GET"}
                      onChange={(e) => setFormData({ ...formData, method: e.target.value as HttpMethod })}
                      label="HTTP Method"
                    >
                      {HTTP_METHODS.map((method) => (
                        <MenuItem key={method} value={method}>
                          {method}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value as Priority })}
                      label="Priority"
                    >
                      {PRIORITIES.map((priority) => (
                        <MenuItem key={priority} value={priority}>
                          {priority.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Autocomplete
                    freeSolo
                    options={developers}
                    disabled={loadingDevelopers}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      const nameStr = [option.first_name, option.last_name].filter(Boolean).join(" ");
                      return nameStr ? `${nameStr} (${option.email || option.username})` : option.email || option.username;
                    }}
                    value={selectedDevOption}
                    onChange={(_, newValue) => setSelectedDevOption(newValue)}
                    onInputChange={(_, newInputValue) => setTypedDevEmail(newInputValue)}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props;
                      const nameStr = [option.first_name, option.last_name].filter(Boolean).join(" ");
                      return (
                        <Box component="li" key={key} {...optionProps} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", py: 1 }}>
                          <Typography variant="body2" fontWeight="600">
                            {nameStr || option.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.email}
                          </Typography>
                        </Box>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assign Backend Developer (Search or type email)"
                        placeholder="Search user or type email (e.g. dev@muzukuru.com)..."
                        slotProps={{ input: { ...params.InputProps, sx: { borderRadius: "12px" } } }}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={3}
                    label="Task / Purpose Description *"
                    placeholder="Describe what data this endpoint should handle and why it is needed..."
                    value={formData.task}
                    onChange={(e) => setFormData({ ...formData, task: e.target.value })}
                    slotProps={{ input: { sx: { borderRadius: "12px" } } }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Additional Notes / Requirements"
                    placeholder="Query params, authentication rules, or special headers required..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    slotProps={{ input: { sx: { borderRadius: "12px" } } }}
                  />
                </Grid>
              </Grid>
            )}

            {/* STEP 1: JSON Payload Editor */}
            {activeStep === 1 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Specify expected request body JSON payload format for backend engineers:
                </Typography>
                <JsonEditorInput
                  value={formData.payload || ""}
                  onChange={(val) => setFormData({ ...formData, payload: val })}
                  label="Request Payload Schema / Sample JSON"
                  minRows={8}
                  maxRows={14}
                />
              </Box>
            )}

            {/* STEP 2: Screenshot & Timeline */}
            {activeStep === 2 && (
              <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <DateTimePicker
                    label="Target Delivery Date"
                    value={targetDate}
                    onChange={(newValue) => setTargetDate(newValue as Dayjs | null)}
                    slotProps={{ textField: { fullWidth: true, sx: { "& .MuiOutlinedInput-root": { borderRadius: "12px" } } } }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <DateTimePicker
                    label="Expected Delivery Date"
                    value={expectedDate}
                    onChange={(newValue) => setExpectedDate(newValue as Dayjs | null)}
                    slotProps={{ textField: { fullWidth: true, sx: { "& .MuiOutlinedInput-root": { borderRadius: "12px" } } } }}
                  />
                </Grid>

                {/* Upload Screenshot Dropzone */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 1 }}>
                    UI Mockup / Screenshot Attachment
                  </Typography>
                  {imagePreview ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: "1px solid rgba(226, 232, 240, 0.8)",
                        borderRadius: "14px",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box
                        component="img"
                        src={imagePreview}
                        alt="Preview"
                        sx={{ width: 80, height: 80, borderRadius: "8px", objectFit: "cover" }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2" fontWeight="700">
                          {imageFile?.name || "Uploaded screenshot"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {imageFile ? `${(imageFile.size / 1024).toFixed(1)} KB` : "Attached"}
                        </Typography>
                      </Box>
                      <IconButton color="error" onClick={handleRemoveImage}>
                        <DeleteIcon />
                      </IconButton>
                    </Paper>
                  ) : (
                    <Paper
                      elevation={0}
                      component="label"
                      sx={{
                        p: 4,
                        border: "2px dashed #CBD5E1",
                        borderRadius: "16px",
                        textAlign: "center",
                        cursor: "pointer",
                        display: "block",
                        backgroundColor: "#F8FAFC",
                        transition: "border-color 0.2s ease",
                        "&:hover": { borderColor: primaryColor },
                      }}
                    >
                      <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                      <CloudUploadIcon sx={{ fontSize: 36, color: "text.secondary", mb: 1 }} />
                      <Typography variant="subtitle2" fontWeight="700">
                        Click to upload UI screenshot
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        PNG, JPG up to 5MB
                      </Typography>
                    </Paper>
                  )}
                </Grid>
              </Grid>
            )}
          </LocalizationProvider>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: "1px solid #E2E8F0", justifyContent: "space-between" }}>
          <Button
            disabled={activeStep === 0}
            onClick={() => setActiveStep((prev) => Math.max(0, prev - 1))}
            startIcon={<ArrowBackIcon />}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
          >
            Previous
          </Button>

          <Box sx={{ display: "flex", gap: 1.5 }}>
            {activeStep < 2 ? (
              <Button
                variant="contained"
                onClick={() => setActiveStep((prev) => prev + 1)}
                endIcon={<ArrowForwardIcon />}
                sx={{ backgroundColor: primaryColor, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
              >
                Next Step
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                sx={{ backgroundColor: primaryColor, borderRadius: "10px", textTransform: "none", fontWeight: 700, px: 3 }}
              >
                Submit Request
              </Button>
            )}
          </Box>
        </DialogActions>

        {/* Add Component Dialog */}
        <Dialog open={openAddComponentDialog} onClose={() => setOpenAddComponentDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Add New Component</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="dense"
              label="Component Name"
              value={newComponentData.name}
              onChange={(e) => setNewComponentData({ ...newComponentData, name: e.target.value })}
            />
            <TextField
              fullWidth
              margin="dense"
              multiline
              rows={2}
              label="Description"
              value={newComponentData.description}
              onChange={(e) => setNewComponentData({ ...newComponentData, description: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAddComponentDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveNewComponent} disabled={savingNewComponent} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>

      {/* Preset Templates Modal */}
      <EndpointTemplatesModal
        open={openTemplatesModal}
        onClose={() => setOpenTemplatesModal(false)}
        onSelectTemplate={handleSelectTemplate}
      />
    </>
  );
};
